import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  InputAdornment,
  Radio,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  useGetPromotionInvitesQuery,
  useSyncInviteEmployeesMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { useAlert } from "../../../hooks/useAlert";
import { formatUtcEventDate } from "./promotionEventDateTime";
import "./AddEmployeesToEventModal.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  outline: "none",
  overflow: "hidden",
};

const formatEventDate = (iso) => {
  if (!iso) return "Date not set";
  const label = formatUtcEventDate(iso, { shortYear: true });
  return label === "—" ? "Date not set" : label;
};

export const AddToCreatedEventModal = ({
  open,
  onClose,
  bookId,
  bookName,
  promotionIds = [],
  onSuccess,
}) => {
  const { displayAlert } = useAlert();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedInviteId, setSelectedInviteId] = useState("");

  const { data, isLoading, isFetching } = useGetPromotionInvitesQuery(
    {
      status: "PROMOTION_CREATED",
      bookId,
      search: debouncedSearch || undefined,
      page: 1,
      limit: 200,
    },
    { skip: !open || !bookId, refetchOnMountOrArgChange: true }
  );

  const [syncEmployees, { isLoading: isSaving }] =
    useSyncInviteEmployeesMutation();

  const events = data?.data?.invites?.list || [];
  const employeeCount = promotionIds.length;

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search, open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
      setSelectedInviteId("");
    }
  }, [open]);

  const handleClose = () => {
    if (isSaving) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!selectedInviteId || !promotionIds.length) return;

    try {
      const result = await syncEmployees({
        inviteId: selectedInviteId,
        promotionIds,
        mode: "add",
      }).unwrap();

      displayAlert({
        open: true,
        message: `${employeeCount} employee${
          employeeCount === 1 ? "" : "s"
        } added to event`,
        type: "success",
        timeout: "2500",
      });
      onSuccess?.(result?.data);
      handleClose();
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to add employees",
        type: "error",
        timeout: "3000",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={isSaving ? undefined : handleClose}
      aria-labelledby="add-to-created-event-title"
    >
      <Box sx={modalStyle} className="add-employees-modal">
        <div className="add-employees-modal__head">
          <div>
            <h2 id="add-to-created-event-title">Add to event</h2>
            <p className="add-employees-modal__subtitle">
              Choose a created event from{" "}
              <span className="add-employees-modal__book">
                {bookName || "this"}
              </span>{" "}
              to add {employeeCount} selected employee
              {employeeCount === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            className="add-employees-modal__close"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="add-employees-modal__separator" />

        <div className="add-employees-modal__search-row">
          <TextField
            fullWidth
            size="small"
            placeholder="Search events by name, venue, or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#98a2b3" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
              },
            }}
          />
        </div>

        <div className="add-employees-modal__list-head">
          <span className="add-employees-modal__list-title">
            Created events ({events.length})
          </span>
        </div>

        <div className="add-employees-modal__list">
          {(isLoading || isFetching) && (
            <p className="add-employees-modal__empty">Loading events…</p>
          )}
          {!isLoading && !isFetching && events.length === 0 && (
            <p className="add-employees-modal__empty">
              No created events available. Use Create Invite to start a new
              event.
            </p>
          )}
          {events.map((row) => {
            const checked = String(selectedInviteId) === String(row._id);
            return (
              <label
                key={row._id}
                className={`add-employees-modal__row ${
                  checked ? "add-employees-modal__row--selected" : ""
                }`}
              >
                <Radio
                  size="small"
                  checked={checked}
                  onChange={() => setSelectedInviteId(row._id)}
                  disabled={isSaving}
                  sx={{
                    color: "var(--grey400)",
                    "&.Mui-checked": { color: "var(--pblue)" },
                  }}
                />
                <div className="add-employees-modal__row-info">
                  <span className="add-employees-modal__name">
                    {row.name || "Untitled event"}
                    {row.eventId ? ` — ${row.eventId}` : ""}
                  </span>
                  <span className="add-employees-modal__dept">
                    {formatEventDate(row.eventDate)}
                    {row.venue ? ` · ${row.venue}` : ""}
                    {` · ${row.employeeCount || 0} employee${
                      (row.employeeCount || 0) === 1 ? "" : "s"
                    }`}
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        <div className="add-employees-modal__footer">
          <span className="add-employees-modal__count">
            <span className="add-employees-modal__count-dot" />
            {selectedInviteId
              ? `${employeeCount} employee${
                  employeeCount === 1 ? "" : "s"
                } will be added`
              : "Select an event"}
          </span>
          <div className="add-employees-modal__actions">
            <Button
              type="button"
              variant="outlined"
              className="add-employees-modal__btn-cancel"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              className="add-employees-modal__btn-confirm"
              disableElevation
              onClick={handleConfirm}
              disabled={isSaving || !selectedInviteId || !promotionIds.length}
            >
              {isSaving ? "Adding…" : "Confirm Selection"}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
